import { useEffect, useState, useRef, Fragment } from "react";
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from "../components/button";
import { SidebarLayoutNew } from "../components/sidebar-layout-new";
import { Heading } from "../components/heading";
import { Footer } from "../components/footer";
import { BreadcrumbsNew } from "../components/breadcrumbs";
import { PageInfo } from "../components/page-info";
import { useAlert } from "../components/customAlert";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Loader } from "../components/loader";
import { useSession } from '../components/session-provider';
import config from "../config.js";

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { RssIcon, ChevronDownIcon, EllipsisHorizontalIcon, PencilSquareIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../components/dropdown'
import WarningAlert from '../components/WarningAlert'
import { InboxArrowDownIcon,MagnifyingGlassIcon   } from "@heroicons/react/20/solid";

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { LinkIcon, PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid'

export function TitlePoliciesForm({options = [],selectedPolicies,setSelectedPolicies,showAlert}) {
  const [newPolicyLabel, setNewPolicyLabel] 			= useState("");
  const [editingPolicy, setEditingPolicy] 				= useState(null); // Track the policy being edited
  const inputRef 										= useRef(null);  
  const scrollContainerRef 								= useRef(null);  
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] 	= useState(false);
  const [loading, setLoading] 							= useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] 	= useState(false);
  const [policyToDelete, setPolicyToDelete] 			= useState(null);
  const [isDefaultPolicy, setIsDefaultPolicy] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const arePoliciesSame = (policy1, policy2) => {
    if (policy1.length !== policy2.length) return false;
    
    // Sort both arrays by label to ensure consistent comparison
    const sorted1 = [...policy1].sort((a, b) => a.label.localeCompare(b.label));
    const sorted2 = [...policy2].sort((a, b) => a.label.localeCompare(b.label));
    
    return sorted1.every((policy, index) => {
      return policy.label === sorted2[index].label && 
             policy.value === sorted2[index].value;
    });
  };
  
  // Check if policies are the same as default whenever either changes
  useEffect(() => {
    setIsDefaultPolicy(arePoliciesSame(selectedPolicies, options));
  }, [selectedPolicies, options]);
  
// Tooltip component
  const Tooltip = ({ children, content, show }) => {
    return (
      // CORRECTED: Mouse event handlers are now on this wrapper div
      <div 
        className="relative inline-flex items-center"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
        {show && (
          <div
            className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-lg"
            role="tooltip"
          >
            {content}
            {/* Tooltip Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    );
  };



  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }; 
  
  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setIsDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (policyToDelete) {
      performDeletePolicy(policyToDelete._id);
      setPolicyToDelete(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setPolicyToDelete(null);
    setIsDeleteDialogOpen(false);
  }; 
	
  const performDeletePolicy = async (id) => {
	setLoading(true);
    try {
      const response = await fetch(`${config.apiPath}test-cycle/test_cycle_ui_handler.php?type=delete_policy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: id }),
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSelectedPolicies((prev) =>
          prev.filter((policy) => policy._id !== id)
        ); 
        showAlert("Policies removed successfully!", "Policies Setup", "success", 3000);
		setLoading(false);
      } else {
        showAlert(result.errorMsg, "Policies Setup", "error", 3000);
		setLoading(false);
      } 
    } catch (error) {
      console.error('Error deleting policy:', error);
      showAlert("Something went wrong. Please try again.", "Policies Setup", "error", 3000);
	  setLoading(false);
    }
  }; 

  // Handle edit policy
  const handleEdit = (opt) => {
    setEditingPolicy(opt);
    setNewPolicyLabel(opt.label);
  };

   const handleSaveEdit = async () => {
    if (!newPolicyLabel.trim()) return;
    const newPolicyValue = newPolicyLabel.toLowerCase();
    setLoading(true);
    try {
      const response = await fetch(`${config.apiPath}test-cycle/test_cycle_ui_handler.php?type=edit_policy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: editingPolicy._id, label: newPolicyLabel, value: newPolicyValue }),
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSelectedPolicies((prev) =>
          prev.map((policy) =>
            policy._id === editingPolicy._id
              ? { ...policy, label: newPolicyLabel, value: newPolicyValue }
              : policy
          )
        );
        setEditingPolicy(null);
        setNewPolicyLabel('');
        showAlert("Policies updated successfully!", "Policies Setup", "success", 3000);
		setLoading(false);
      } else {
        showAlert(result.errorMsg, "Policies Setup", "error", 3000);
		setLoading(false);
      } 
    } catch (error) {
      console.error('Error editing policy:', error);
      showAlert("Something went wrong. Please try again.", "Policies Setup", "error", 3000); 
	  setLoading(false);
    }
  }; 

  // Handle add new policy
  const handleAddPolicy = async () => {
    const trimmedLabel = newPolicyLabel.trim();
    if (!trimmedLabel) return;

    const newPolicyValue = trimmedLabel.toLowerCase();
    const newPolicy = { label: trimmedLabel, value: newPolicyValue };
    setLoading(true);
    try {
      const response = await fetch(`${config.apiPath}test-cycle/test_cycle_ui_handler.php?type=add_policy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newPolicy),
      }); 
      const result = await response.json(); 
      if (result.status === 'success') { 
        setSelectedPolicies((prev) => [...prev, { ...newPolicy, _id: result.policyId }]);  
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setTimeout(() => {
          focusInput();
          scrollToBottom();
        }, 300);
        showAlert("Policies added successfully!", "Policies Setup", "success", 3000);
		setLoading(false);
      } else {
        showAlert(result.errorMsg, "Policies Setup", "error", 3000);
		setLoading(false);
      }	  
    } catch (error) {
      console.error('Error adding policy:', error);
      showAlert("Something went wrong. Please try again.", "Policies Setup", "error", 3000);
	  setLoading(false);
    }
  };

  // Function to handle reset to default policies
  const handleResetPolicies = () => { 
    setIsConfirmDialogOpen(true);
  }; 
  // Function to confirm reset action
  const handleConfirmReset = async () => { 
    try {
      setIsConfirmDialogOpen(false); // Close the dialog
      setLoading(true); 

      const response = await fetch(`${config.apiPath}test-cycle/test_cycle_ui_handler.php?type=reset_policy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(options),
      });

      const result = await response.json(); // Await the response to get the JSON result 
      if (result.status === "success") {  
        setSelectedPolicies(result.polityList); // Ensure selected policies are reset to default
        setTimeout(() => {
			setLoading(false); // Stop loading spinner
			showAlert("Policies reset successfully!", "Policies Setup", "success", 3000);
		}, 200); // Give it a small timeout before stopping the loading 
      } else {
        showAlert(result.errorMsg, "Policies Setup", "error", 3000);
        setLoading(false);
      }
    } catch (error) {
      showAlert("Something went wrong. Please try again.", "Policies Setup", "error", 3000);  
      setLoading(false);
    }  finally {
      // FIX: Hide the tooltip after the dialog action is complete
      setShowTooltip(false);
    }
  };


  // Function to cancel reset action
  const handleCancelReset = () => {
    setIsConfirmDialogOpen(false); // Close the dialog without doing anything
	// FIX: Hide the tooltip when the dialog is cancelled
    setShowTooltip(false);
  }; 
   

  return (
    <div className="max-w-6xl">
      {/* Heading */}
	 
	  <div className="flex items-center justify-between mb-4 mt-4" >
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Configure Title Policies
        </h2>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Select rules to enforce when generating product titles.
        </p>
      </div>
	   
 <Tooltip 
          content="Both Default and Custom selected Policy rules are same" 
          show={isDefaultPolicy && showTooltip}
        >
          <Button
            color="zinc"
            type="button"
            onClick={handleResetPolicies}
            className={`text-gray-500 border border-gray-300 hover:bg-gray-200 ${
              isDefaultPolicy ? 'cursor-not-allowed opacity-60' : ''
            }`}
            disabled={isDefaultPolicy}
          >
            Reset to Default Policies
          </Button>
        </Tooltip>
	  
	  
	  
	  </div>
	  
      {/* Add New Policy */}
      <div className="flex items-center space-x-4 mb-6">
        <input
	      id="add_rule"
          ref={inputRef}
          type="text" 
          onChange={(e) => setNewPolicyLabel(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm dark:bg-zinc-800 dark:text-white dark:border-gray-700 w-full"
          placeholder="Enter new policy label"
		  disabled={!!editingPolicy}
        />
        <button
          type="button"
          onClick={handleAddPolicy}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm"
		  disabled={!!editingPolicy}
        >
          Add
        </button>
      </div>

      {/* Selected Policies List */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-zinc-900 shadow-sm">
        <div
          ref={scrollContainerRef}
          className="max-h-72 overflow-y-auto p-4 space-y-4"
        >
          {selectedPolicies.map((policy) => {
  const isEditing = editingPolicy?._id === policy._id;

  return (
    <div
      key={policy._id}  // Changed to _id here
      className="flex justify-between items-center p-2 rounded-md bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700"
    >
      <span
        className={`text-sm dark:text-white w-4/5 ${isEditing ? "text-gray-500" : "text-gray-900"}`}
      >
        {isEditing ? (
          <textarea
            value={newPolicyLabel}
            onChange={(e) => setNewPolicyLabel(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md w-full h-16 resize-none" 
          />
        ) : (
          policy.label
        )}
      </span>

      {/* Edit & Delete */}
      <div className="flex space-x-2">
        {!isEditing ? (
          <>
            <button
              type="button"
              onClick={() => handleEdit(policy)}
              className="text-blue-600 text-sm hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => handleDeleteClick(policy)}   
              className="text-red-600 text-sm hover:underline"
            >
              Delete
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={handleSaveEdit}
              className="text-green-600 text-sm hover:underline"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingPolicy(null);
                setNewPolicyLabel("");
              }}
              className="text-red-600 text-sm hover:underline"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
})}

        </div>
      </div>
      {loading && <Loader />}
      {/* Custom Confirm Dialog */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onConfirm={handleConfirmReset}
        onCancel={handleCancelReset}
        message="Are you sure you want to reset to the default policies? This action will remove all your current rule policies and restore the default settings."
        title = "Confirm Restore Default Policies"
 	  />
      <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          message={`Are you sure you want to delete the policy "${
            policyToDelete?.label || ""
          }"?`}
		  title = "Confirm Policy Deletion"
        />
    </div> 
  );
}
export function SupplementalFeedNew() {
  let arrWarning  = [];
  const defaultFeedName = "Supplemental AI Feed - FeedSumo";
  const [feedName, SetFeedName] = useState(defaultFeedName);
  const [loading, setLoading] 							= useState(false);
  const { session,setSession} = useSession();
  var currentCompany = session.sumo_account_id;
  arrWarning.push("Supplemental feed is not yet created.");
  arrWarning.push("Once the title starts generating, we will create the supplemental feed.");
  arrWarning.push("Once supplemental feed created, you can see feed details and feed pushing history here.");
  const [enableEdit, setEnableEdit] = useState(false);
  const { showAlert } = useAlert();
  const feedNameValidate = (str) => {
    // 1. Check not empty or whitespace only
    if (!str.trim()) {
     return "Feed name should not be empty";
    }
    const hasLetter = /[a-zA-Z]/.test(str);
    if(hasLetter === false) {
      return "Feed name should contain letters.";
    }
    const startsWithLetter = (string) => /^[A-Za-z]/.test(string);
    if(startsWithLetter(str) === false) {
      return "Feed name should start with a letter.";
    }
    return "";
  };
  const updateFeedName = async() => {
    const errorValidate = feedNameValidate(feedName);
    if(errorValidate !== "") {
      showAlert(errorValidate, "", "error",3000);
      return;
    }
    else {
      setEnableEdit(false);
      setLoading(true);
        try {            
            const response = await fetch(`${config.apiPath}/supp_feed/supp_feed_handler.php?process=update_supp_name&sumo_account_id=${currentCompany}&name=${feedName}`, {
                credentials: 'include'                
            });
            const result = await response.json();
            
             if (result.status === "success") 
             {  
                  SetFeedName(result.supp_feed_name); // Ensure selected policies are reset to default
                  setTimeout(() => {
                    setLoading(false); // Stop loading spinner
                    showAlert("Feed name changed successfully!", "Feed Name Update", "success", 3000);
                  }, 200); // Give it a small timeout before stopping the loading 
             } else {
                  showAlert(result.msg, "Error", "error", 3000);
                  setLoading(false);
                  SetFeedName(defaultFeedName);
             }            
        }catch (err) {            
          setLoading(false);
          SetFeedName(defaultFeedName);
          showAlert('Feed name update failed', "Error" , "error",3000);			
        }
      
    }
  };
  return (
    <>
    <div className="mt-2">
        <WarningAlert title="For your information" warning={arrWarning} />
        <div>
          <div className="px-4 sm:px-0">
            <h3 className="text-base/7 font-semibold text-gray-900 dark:text-white">Feed name Settings</h3>
            <p className="max-w-2xl text-sm/6 text-gray-500 dark:text-gray-400">you can change the feed name to whatever you prefer.</p>
          </div>
          <div className="mt-6 border-t border-gray-100 dark:border-white/10">
            <dl className="divide-y divide-gray-100 dark:divide-white/10">
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900 dark:text-gray-100">Feed name</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-400 flex">
                <div className={`flex rounded-md ${enableEdit === false ? "bg-zinc-100" : ""} bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:max-w-md`}>
                  <input id="feed_name" name="feed_name" value={feedName} type="text" className={`${enableEdit === false ? "cursor-not-allowed" : ""} min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 focus:outline-none sm:text-sm/6`} onChange={(e) => SetFeedName(e.target.value)} disabled={enableEdit === false}/>
                </div>
                <button type="button" className={`text-blue-600 text-sm hover:underline ml-2 ${enableEdit === false ? "block" : "hidden"}`} onClick={() => setEnableEdit(true)}>Change Feed name</button>
                <button type="button" className={`text-blue-600 text-sm hover:underline ml-2 ${enableEdit === true ? "block" : "hidden"}`} onClick={() => updateFeedName()}>Update</button>
                </dd>
              </div>
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"></div>
            </dl>
          </div>
        </div>
    </div> 
    {loading && <Loader />} 
    </>  
  );
}
export function SupplementalFeedNew3({feedDetails,setSupFeedDetails,lastPushHistory,getLastPushHistory,fullHistory,getfullHistory}) {
  //const push_type = feedDetails?.type || '';
  //console.log('push_type : '+push_type);
  const [push_type, setPushType] = useState(feedDetails?.type || '');
  const sectionRef = useRef(null);
  const [manualPushConfirm, setManualPushConfirm] 	= useState(false);
  const [showConfirm, setShowConfirm] 	= useState(false);
  const [newPushType, setNewPushType] 	= useState(null);
  const [showHistory, setShowHistory]   = useState(false);
  const [loading, setLoading] 				  = useState(false);
  const [pauseActiveConfirm, setPauseActiveConfirm] = useState(false);
  const [msgPauseActive,setMsgPauseActive] = useState('');
  const [titlePauseActive,setTitlePauseActive] = useState('');
  const [need_todo,setNeedToDo] = useState('');//puase - active
  const [historyProducts,setHistoryProducts] = useState('');
  const [historyTitle,setHistoryTitle] = useState('');

  const [open, setOpen] = useState(false) 

  const { showAlert } = useAlert();
  const { session, setSession } = useSession();
  var currentCompany = session.sumo_account_id;
  var user_id = session.user_id;
  console.log(session);
  const showHideHistory = () => {
    if(showHistory === true)
      setShowHistory(false);
    else {
      setShowHistory(true);
      setTimeout(() => {
        handleScroll();
      }, 300);
    }
  };

  const smoothScroll = (targetY, duration = 600) => {
    const startY = window.scrollY;
    const diff = targetY - startY;
    let start;

    const step = timestamp => {
      if (!start) start = timestamp;
      const time = timestamp - start;
      const percent = Math.min(time / duration, 1);

      // Ease-in-out
      const ease = percent < 0.5
        ? 2 * percent * percent
        : -1 + (4 - 2 * percent) * percent;

      window.scrollTo(0, startY + diff * ease);

      if (time < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };
  const handleScroll = () => {
    const top = sectionRef.current.offsetTop;
    smoothScroll(top, 900); // 900ms
  };

  const showConfirmPushType = (type_val) => {
    console.log('type_val : '+type_val);
    setNewPushType(type_val);
    console.log(`newPushType : ${newPushType}`);
    setShowConfirm(true);
  }

  
  // Function to cancel type change confirm action
  const handleCancelTypeChange = () => {
    setShowConfirm(false); // Close the dialog without doing anything
    setNewPushType(null);
  }; 

  const handlePauseActivate = () => {
    setPauseActiveConfirm(true);
    if (feedDetails.status === 'active') {
      setNeedToDo('inactive');setTitlePauseActive('Confirm Pause Feed');setMsgPauseActive('Are you sure, you want to pause supplementary feed?');
    }else{
      setNeedToDo('active');setTitlePauseActive('Confirm Activate Feed');setMsgPauseActive('Are you sure, you want to activate supplementary feed ?');
    }
  }  

  const handleCancelPauseActive = () =>{
    setPauseActiveConfirm(false);
    setNeedToDo('');setTitlePauseActive('');setMsgPauseActive('');
  }

  const handleConfirmPauseActive = async() => {
    try {
      setPauseActiveConfirm(false);           
      setLoading(true); 

      const response = await fetch(`${config.apiPath}supp_feed/supp_feed_handler.php?process=pause_active_feed&need_todo=${need_todo}&updated_by=${user_id}&sumo_account_id=${currentCompany}`, { 
        credentials: "include"
      });
      const result = await response.json(); // Await the response to get the JSON result 
	  
      if (result.status === "success") {         
        setTimeout(() => {
			    setLoading(false); // Stop loading spinner

          setSupFeedDetails(prev => ({
            ...prev,
            status: need_todo
          }));

			    showAlert(`Supplementary feed ${result.action} successfully!`, "Success", "success", 3000);
            
            setNeedToDo('');setTitlePauseActive('');setMsgPauseActive('');
		    }, 200); // Give it a small timeout before stopping the loading 
      } else {
        showAlert(result.msg, "Supplementary feed not updated!", "error", 3000);
        setLoading(false);
        setNeedToDo('');setTitlePauseActive('');setMsgPauseActive('');
      }
    } catch (error) {
      setLoading(false);
      showAlert("Something went wrong. Please try again.", "Error", "error", 3000);  
      
      //setNewPushType(null);
      setPauseActiveConfirm(false);
      setNeedToDo('');setTitlePauseActive('');setMsgPauseActive('');
    }
  }

  const handleManualPush = () =>{
    console.log('manual push initiated');
    setManualPushConfirm(true);
  };

  const handleConfirmManualPush = async() =>{    
    try {
      setManualPushConfirm(false);           
      setLoading(true); 

      const response = await fetch(`${config.apiPath}supp_feed/supp_update_cron.php?process=add_supp_feed&pushed_by=${user_id}&sumo_account_id=${currentCompany}`, { 
        credentials: "include"
      });
      const result = await response.json(); // Await the response to get the JSON result 
	  
      if (result.status === "success") {         
        setTimeout(() => {
			    setLoading(false); // Stop loading spinner 
            getLastPushHistory();//get updated last history
            getfullHistory();//get updated full history 
			      showAlert("Supplementary feed pushed to GMC successfully!", "Success", "success", 3000);
            
		      }, 200); // Give it a small timeout before stopping the loading 
      } else {
        showAlert(result.msg, "Supplementary feed not updated!", "error", 3000);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      showAlert("Something went wrong. Please try again.", "Error", "error", 3000);  
      
      //setNewPushType(null);\
      setManualPushConfirm(false);
    }  
  };

  const handleCancelManualPush = ()=>{
      setManualPushConfirm(false);
  };

  const handleConfirmTypeChange = async() => {        
    try {
      setShowConfirm(false);//close confirmbox
      console.log('confirmed');
     
      setLoading(true); 

      const response = await fetch(`${config.apiPath}supp_feed/supp_feed_handler.php?process=change_push_type&sumo_account_id=${currentCompany}&type=${newPushType}`, { 
        credentials: "include"
      });
      const result = await response.json(); // Await the response to get the JSON result 
	  
      if (result.status === "success") { 
        setPushType(result.push_type);
        setNewPushType(result.push_type); // Ensure selected policies are reset to default
        setTimeout(() => {
			    setLoading(false); // Stop loading spinner

            //update type in state (parent)
            setSupFeedDetails(prev => ({
              ...prev,
              type: result.push_type
            }));

			      showAlert("Push Type Updated Successfully!", "Push Type Update", "success", 3000);
		      }, 200); // Give it a small timeout before stopping the loading 
      } else {
        showAlert(result.msg, "Push Type Update", "error", 3000);
        setLoading(false);
      }
    } catch (error) {
      showAlert("Something went wrong. Please try again.", "Push Type Update", "error", 3000);  
      setLoading(false);
      //setNewPushType(null);
    }    
    
  };

  const handleViewProductHistory = async(unique_id,pushed_at)=>{
      try {  
      setLoading(true); 

      const response = await fetch(`${config.apiPath}supp_feed/supp_feed_handler.php?process=get_history_prdts&unique_id=${unique_id}&sumo_account_id=${currentCompany}`, { 
        credentials: "include"
      });
      const result = await response.json(); // Await the response to get the JSON result 
	  
      if (result.status === "success") { 
        setHistoryProducts(result.products);  
        setHistoryTitle('Feed Push History - '+pushed_at);             
        setTimeout(() => {
			    setLoading(false); // Stop loading spinner 
			      setOpen(true);
		      }, 200); // Give it a small timeout before stopping the loading 
      } else {
        showAlert(result.msg, "Unable to fetch history", "error", 3000);
        setLoading(false);
        setOpen(false);
      }
    } catch (error) {
      showAlert("Something went wrong. Please try again.", "Push History", "error", 3000);  
      setLoading(false); 
      setOpen(false);     
    }
  };

  return (
    <>
    <div className="mt-2">
        <ul role="list" className="grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-2 xl:gap-x-8">
        <li key="current" className="overflow-hidden rounded-xl outline outline-gray-200 dark:-outline-offset-1 dark:outline-white/10">
          <div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6 dark:border-white/10 dark:bg-gray-800/50">
            <div className="text-sm/6 font-medium text-gray-900 dark:text-white">Feed Details</div>
            <Menu as="div" className="relative ml-auto">
                <MenuButton className="relative block text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-white">
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Open options</span>
                    <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
                </MenuButton>
              <MenuItems transition className="absolute right-0 z-10 mt-0.5 w-max origin-top-right rounded-md bg-white py-2 shadow-lg outline-1 outline-gray-900/5 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">
                <MenuItem>
                  {feedDetails.status==='active' ?(
                  <a onClick={handlePauseActivate} href="#" className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden dark:text-white dark:data-focus:bg-white/5">
                    Pause
                  </a>):(
                  <a onClick={handlePauseActivate} href="#" className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden dark:text-white dark:data-focus:bg-white/5">
                    Activate
                  </a>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>

          <dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6 dark:divide-white/10">
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Name</dt>
              <dd className="text-gray-700 dark:text-gray-300">{feedDetails.supp_feed_display_name}</dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Push Type</dt>
              <dd className="text-gray-700 dark:text-gray-300">{push_type?.charAt(0).toUpperCase() + push_type?.slice(1)}</dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Status</dt>
              <dd className={`dark:text-gray-300 font-medium ${feedDetails.status==='active' ? 'text-green-700' :'text-red-700'}`}>
                {feedDetails.status?.charAt(0).toUpperCase() + feedDetails.status?.slice(1)}</dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Created On</dt>
              <dd className="text-gray-700 dark:text-gray-300">{feedDetails.created_on}</dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">
                <Dropdown>
                    <DropdownButton color="dark">
                        Push Type
                        <ChevronDownIcon />
                    </DropdownButton>
                    <DropdownMenu>
                        <DropdownItem onClick={() => showConfirmPushType("auto")} disabled={push_type === "auto"} className={push_type==='auto'? 'cursor-not-allowed bg-blue-400 rounded-md dark:bg-blue-600 !text-white':''}>Automatic Push</DropdownItem>
                        <DropdownItem onClick={() => showConfirmPushType("manual")} disabled={push_type === "manual"} className={push_type==='manual'? 'cursor-not-allowed bg-blue-400 rounded-md dark:bg-blue-600 !text-white':''}>Manual Push</DropdownItem>
                    </DropdownMenu>
                </Dropdown>
                { push_type === "manual" && feedDetails.status==='active' &&
                <Button onClick={handleManualPush} className="-my-0.5 inline-flex justify-end ml-[10px]">Push To GMC</Button> }
                
              </dt>
            </div>
          </dl>
        </li>
        {/*lastPushHistory.created_on &&*/}
        <li key="current" className="overflow-hidden rounded-xl outline outline-gray-200 dark:-outline-offset-1 dark:outline-white/10">
          <div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6 dark:border-white/10 dark:bg-gray-800/50">
            <div className="text-sm/6 font-medium text-gray-900 dark:text-white">Last Push Details</div>
            <Menu as="div" className="relative ml-auto">
                <MenuButton className="relative block text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-white">
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Open options</span>
                    <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
                </MenuButton>
              <MenuItems transition className="absolute right-0 z-10 mt-0.5 w-max origin-top-right rounded-md bg-white py-2 shadow-lg outline-1 outline-gray-900/5 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">
                <MenuItem>
                  <a href={`${config.apiPath}supp_feed/`+lastPushHistory.history_excel_path} target="_blank" className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden dark:text-white dark:data-focus:bg-white/5">
                    Download Excel
                  </a>
                </MenuItem>
                <MenuItem>
                  <a href={`${config.apiPath}supp_feed/`+lastPushHistory.history_zip_path} target="_blank" className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden dark:text-white dark:data-focus:bg-white/5">
                    Download ZIP
                  </a>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
          <dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6 dark:divide-white/10">
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Last Push Count</dt>
              <dd className="text-gray-700 dark:text-gray-300">
                <div className="rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset dark:bg-green-500/10 dark:text-green-500 dark:ring-green-500/10">
                    {lastPushHistory?.pushed_cnt || ''}
                </div>
              </dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Last Pushed Date</dt>
              <dd className="text-gray-700 dark:text-gray-300">{lastPushHistory?.created_on || ''}</dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Pushed By</dt>
              <dd className="text-gray-700 dark:text-gray-300">{lastPushHistory?.pushed_by?
              lastPushHistory.pushed_by?.charAt(0).toUpperCase() + lastPushHistory.pushed_by?.slice(1)
              :''}</dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">Success Count</dt>
              <dd className="text-gray-700 dark:text-gray-300">
                <div className="rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset dark:bg-green-500/10 dark:text-green-500 dark:ring-green-500/10">
                    {lastPushHistory?.success_cnt || ''}
                </div>
              </dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-gray-500 dark:text-gray-400">
                <Button className="-my-0.5" onClick={() => showHideHistory()}>History</Button>
              </dt>
            </div>
          </dl>
        </li>
        {/**/}
    </ul>
    <div className={`mt-5 ${showHistory === true ? "block" : "hidden"}`} ref={sectionRef}>
      <div className="px-4 sm:px-0 mb-3">
        <h3 className="text-base/7 font-semibold text-gray-900 dark:text-white">Feed Pushing History</h3>
        <p className="max-w-2xl text-sm/6 text-gray-500 dark:text-gray-400">You can view the last 20 feed push details here.</p>
      </div>
      <ul role="list" className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2 xl:gap-x-8">
        {fullHistory.length === 0 && <p>No Results Found.</p>}
        {fullHistory.map((item, idx) => (
        <li key="first" className="col-span-1 divide-y divide-gray-200 overflow-hidden rounded-xl outline outline-gray-200 dark:-outline-offset-1 dark:outline-white/10">
          <div className="flex w-full items-center justify-between space-x-6 p-4">
            <div className="flex-1 truncate">
              <div className="flex items-center space-x-3">
                <h3 className="truncate text-sm font-medium text-gray-900 dark:text-white">Pushed Count : {item.pushed_cnt}</h3>
                {item.failure_cnt >0 ?(
                <>
                <span className="inline-flex shrink-0 items-center rounded-full bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 inset-ring inset-ring-red-600/20 dark:bg-red-500/10 dark:text-red-500 dark:inset-ring-red-500/10">
                  Issue
                </span>
                <p className="truncate text-sm text-red-500 dark:text-gray-400 ml-auto">Failed Count : {item.failure_cnt}</p>
                </>                
                ):(
                <span className="inline-flex shrink-0 items-center rounded-full bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 inset-ring inset-ring-green-600/20 dark:bg-green-500/10 dark:text-green-500 dark:inset-ring-green-500/10">
                  Success
                </span>
                )}
              </div>
              <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">Pushed At : {item.created_on}</p>
            </div>
          </div>
          <div>
            <div className="-mt-px flex divide-x divide-gray-200 dark:divide-white/10">
              <div className="flex w-0 flex-1">
                <a href={`${config.apiPath}supp_feed/`+item.history_excel_path} target="_blank" className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-3 text-sm font-semibold text-gray-900 dark:text-white cursor-pointer">
                  <InboxArrowDownIcon aria-hidden="true" className="size-5 text-gray-400 dark:text-gray-500" />
                  Excel
                </a>
              </div>
              <div className="-ml-px flex w-0 flex-1">
                <a href={`${config.apiPath}supp_feed/`+item.history_zip_path} target="_blank" className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-3 text-sm font-semibold text-gray-900 dark:text-white cursor-pointer">
                  <InboxArrowDownIcon aria-hidden="true" className="size-5 text-gray-400 dark:text-gray-500" />
                  ZIP
                </a>
              </div>
              <div className="-ml-px flex w-0 flex-1">
                <a onClick={() => handleViewProductHistory(item.unique_id,item.created_on)} className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-3 text-sm font-semibold text-gray-900 dark:text-white cursor-pointer">
                  <MagnifyingGlassIcon aria-hidden="true" className="size-5 text-gray-400 dark:text-gray-500" />
                  View
                </a>
              </div>
            </div>
          </div>
        </li>
        ))}        
      </ul>
    </div>
    </div>

    {loading && <Loader />} 

    <ConfirmDialog
        isOpen={showConfirm}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        message={`Are you sure you want to change push type as ${newPushType ? newPushType.charAt(0).toUpperCase() + newPushType.slice(1) : ''} ?`}
        title = "Confirm Push Type Change"
 	  />

    <ConfirmDialog
        isOpen={manualPushConfirm}
        onConfirm={handleConfirmManualPush}
        onCancel={handleCancelManualPush}
        message={`Are you sure you want to push supplemetary feed to GMC ?`}
        title = "Confirm GMC Push"
 	  />

    <ConfirmDialog
        isOpen={pauseActiveConfirm}
        onConfirm={handleConfirmPauseActive}
        onCancel={handleCancelPauseActive}
        message={msgPauseActive}
        title = {titlePauseActive}
 	  />

    <div>
      {/*<button
        onClick={() => setOpen(true)}
        className="rounded-md bg-gray-950/5 px-2.5 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-950/10 dark:bg-white/10 dark:text-white dark:inset-ring dark:inset-ring-white/5 dark:hover:bg-white/20"
      >
        Open drawer
      </button>*/}
      <Dialog open={open} onClose={setOpen} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0 dark:bg-gray-900/50"
        />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
              >
                <form className="relative flex h-full flex-col overflow-y-auto bg-white shadow-xl dark:bg-gray-800 dark:after:absolute dark:after:inset-y-0 dark:after:left-0 dark:after:w-px dark:after:bg-white/10">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="bg-gray-50 px-4 py-6 sm:px-6 dark:bg-gray-800/50">
                      <div className="flex items-start justify-between space-x-3">
                        <div className="space-y-1">
                          <DialogTitle className="text-base font-semibold text-gray-900 dark:text-white">
                            {historyTitle || ''}
                          </DialogTitle>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            The titles we pushed to the GMC are listed below.
                          </p>
                        </div>
                        <div className="flex h-7 items-center">
                          <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:hover:text-white dark:focus-visible:outline-indigo-500"
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon aria-hidden="true" className="size-6" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Divider container */}
                    <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0 dark:sm:divide-white/10">
                      {/* Project name 
                      <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                        <div>
                          <label
                            htmlFor="project-name"
                            className="block text-sm/6 font-medium text-gray-900 sm:mt-1.5 dark:text-white"
                          >
                            Project name
                          </label>
                        </div>
                        <div className="sm:col-span-2">
                          <input
                            id="project-name"
                            name="project-name"
                            type="text"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
                          />
                        </div>
                      </div> */}


                      <div className="px-4 sm:px-6 lg:px-8">      
                        <div className="mt-8 flow-root">
                          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                              <table className="relative min-w-full divide-y divide-gray-300 dark:divide-white/15">
                                <thead>
                                  <tr>
                                    <th
                                      scope="col"
                                      className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white"
                                    >
                                      Product ID
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                      Updated Title
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                      Status
                                    </th>                 
                                    
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-white/10">                                  
                                    {Object.values(historyProducts).map((prdt)=>(
                                    <tr key={prdt.id}>
                                      <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-white">
                                        {prdt.id}
                                      </td>
                                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                                        {prdt.title}
                                      </td>
                                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                                        {prdt.status}
                                      </td>                    
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-gray-100 dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20"
                      >
                        Close
                      </button>                      
                    </div>
                  </div>
                </form>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>

    </>
  );
}

export default function CycleConfig() {  
  const { showAlert } = useAlert();
  const [activeTab, setActiveTab] = useState("titlePolicies");
  const [policyList, setPolicyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState([]);  
  const [supFeedDetails, setSupFeedDetails] = useState([]);
  const [supFeedCount, setSupFeedCount] = useState(0);
  const [lastPushHistory, setLastPushHistory] = useState([]);
  const [fullHistory, setFullHistory] = useState([]); 
  const navigate = useNavigate(); 
   
  const { session, setSession } = useSession();
  var currentCompany = session?.sumo_account_id ?? "";
  const currentCompanyName = session?.company ?? "";

  const breadcrumbsLink = [{ name: currentCompanyName, href: "/title-test", current: false },{ name: "Settings", href: "#", current: true }];
  
  const page_info = `This section allows you to manage and configure different system settings, such as account management, user permissions, title policies, and supplemental feed. `;
  const tabs = [     
    { id: "titlePolicies", name: "Generic Title Policies" },
    { id: "supplemental_feed", name: "Supplemental Feed" },
  ];
  //
  
  const getDefaultPolicy = async () => {
      try {
        const policy = await fetch(
          `${config.apiPath}test-cycle/test_cycle_ui_handler.php?type=get_default_policy`,
          { method: "GET", credentials: "include" }
        );
        const data = await policy.json();
        if (data.status === "error" && data.errMsg === "Invalid Loign Data") {
          window.location.href = `/${config.appPath}`;
        } else {
          if (data.polityList !== "") {
            setPolicyList(data.polityList);
          }
        }
      } catch (error) {
        console.error("Error fetching ads status:", error);
      }
    };

    const clientPolicy = async () => {
        try {
          const policy = await fetch(
            `${config.apiPath}test-cycle/test_cycle_ui_handler.php?type=get_client_policy`,
            { method: "GET", credentials: "include" }
          );
          const data = await policy.json();
          if (data.status === "error" && data.errMsg === "Invalid Loign Data") {
            window.location.href = `/${config.appPath}`;
          } else {
            if (data.polityList !== "") {
              setSelectedPolicies(data.polityList);
            }
          }
        } catch (error) {
          console.error("Error fetching ads status:", error);
        }
      };

    const fetchSuppFeedDetails = async () => {      
      try {
          setLoading(true); 
          const sup_feeds = await fetch(`${config.apiPath}/supp_feed/supp_feed_handler.php?process=get_sup_feed_details&sumo_account_id=${currentCompany}`,{ credentials: 'include' });
          const data = await sup_feeds.json();
          if (data.status === "error" && data.msg) {
            setSupFeedDetails([]);
            setSupFeedCount(0);
          }else{
            setSupFeedDetails(data.result);
            setSupFeedCount(data.total);
          }        
      }catch(error){
        //console.error("Fetch error", error);
        showAlert("Something went wrong. Please try again.", "Supplementary Feeds", "error", 3000);
      }finally {
        setLoading(false); // hide loader
      }
    };

    const getLastPushHistory = async () => {       
      try {
        setLoading(true);
          const last_history = await fetch(`${config.apiPath}/supp_feed/supp_feed_handler.php?process=get_last_push_history&sumo_account_id=${currentCompany}`,{ credentials: 'include' });
          const data = await last_history.json();
          if (data.status === "error" && data.msg) {
            setLastPushHistory([]);          
          }else{
            setLastPushHistory(data.result);          
          }        
      }catch(error){
        //console.error("Fetch error", error);
        showAlert("Something went wrong. Please try again.", "Supplementary Feeds", "error", 3000);
      }finally {
        setLoading(false); // hide loader
      }
    };

    const getfullHistory = async () => {       
      try {
        setLoading(true);
          const full_history = await fetch(`${config.apiPath}/supp_feed/supp_feed_handler.php?process=get_full_history&sumo_account_id=${currentCompany}`,{ credentials: 'include' });
          const data = await full_history.json();
          if (data.status === "error" && data.msg) {
            setFullHistory([]);          
          }else{
            setFullHistory(data.result);          
          }        
      }catch(error){
        //console.error("Fetch error", error);
        showAlert("Something went wrong. Please try again.", "Supplementary Feeds", "error", 3000);
      }finally {
        setLoading(false); // hide loader
      }
    };

  {/*useEffect(() => {
    getDefaultPolicy();
    clientPolicy();

    fetchSuppFeedDetails();
    getLastPushHistory();
    getfullHistory();
  }, []);*/}

  useEffect(() => {
    async function loadAll() {
      setLoading(true);

      await Promise.all([
        getDefaultPolicy(),
        clientPolicy(),
        fetchSuppFeedDetails(),
        getLastPushHistory(),
        getfullHistory()
      ]);

      setLoading(false);
    }

    loadAll();
  }, []);

  return (
    <Fragment>
      <SidebarLayoutNew selected_menu="tabs">
        {/* Breadcrumb + Header */}
        <BreadcrumbsNew pages={breadcrumbsLink} />

        <div className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/4 font-medium text-zinc-950 sm:text-sm/4 dark:text-white">
          <Heading>{tabs.find((tab) => tab.id === activeTab)?.name}</Heading>
          <div className="flex items-center gap-2 ml-auto">
            <Button className="flex items-center gap-1" onClick={() => navigate("/title-test")}>
                Back
            </Button>
          </div>
        </div>

        {/* PageInfo */}
        <PageInfo info={page_info} />

        {/* Tabs */}
        <div className="w-full max-w-6xl">
          <div className="border-b border-gray-200 flex space-x-8">
            {tabs.map((tab) => {
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.name}
                </button>
              );
            })}
          </div>

          {loading && <Loader />}

          {/* Tab content */}
          <div className="p-2 space-y-4">
            {activeTab === "titlePolicies" && (
              <TitlePoliciesForm
                options={policyList}
                selectedPolicies={selectedPolicies}
                setSelectedPolicies={setSelectedPolicies}
                showAlert={showAlert}
              />
            )}
            {activeTab !== "titlePolicies" && supFeedCount >0 && (
              <SupplementalFeedNew3 
              feedDetails={supFeedDetails||{}}
              setSupFeedDetails={setSupFeedDetails}
              lastPushHistory={lastPushHistory||{}}
              getLastPushHistory={getLastPushHistory}
              fullHistory={fullHistory||{}}
              getfullHistory={getfullHistory}
             />
            )}
            {activeTab !== "titlePolicies" && supFeedCount ===0 && (
              <SupplementalFeedNew/>
            )}
            {/*activeTab !== "titlePolicies" && currentCompany === "aspinal_of_london_us" && (
              <SupplementalFeedNew/>
            )*/}
            {/*activeTab !== "titlePolicies" && currentCompany !== "aspinal_of_london_us" && currentCompany !== "hobbycraft_gb" && (
              <SupplementalFeedNew/>
            )*/}
          </div>
        </div> 
      </SidebarLayoutNew>
      <Footer />
    </Fragment>
  );
}
